import React from 'react';
import { Box, Select, MenuItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DbStatus } from '@/types/api';
import { useUpdateAnnotation } from '@/hooks/useChannelAnnotations';
import { DB_STATUS_COLORS, DB_STATUS_VALUES, dbStatusLabelKey } from '@/utils/dbStatus';
import { useChatTheme } from '../ChatThemeContext';

interface DbStatusSelectorProps {
  chatId: number;
  currentStatus: DbStatus;
  /**
   * `header` paints for the blue chat-preview header bar; `plain` keeps the MUI
   * theme, which is what a table cell outside ChatThemeProvider needs.
   */
  variant?: 'header' | 'plain';
}

const DbStatusSelector: React.FC<DbStatusSelectorProps> = ({
  chatId,
  currentStatus,
  variant = 'header',
}) => {
  const { t } = useTranslation('pages');
  const { colors } = useChatTheme();
  const updateMutation = useUpdateAnnotation();

  const handleChange = (newStatus: DbStatus) => {
    updateMutation.mutate({ chatId, data: { db_status: newStatus } });
  };

  return (
    <Select
      value={currentStatus}
      onChange={(e) => handleChange(e.target.value as DbStatus)}
      size="small"
      sx={{
        minWidth: 140,
        height: 32,
        ...(variant === 'header'
          ? {
              color: colors.headerText,
              '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
              '.MuiSvgIcon-root': { color: colors.headerText },
            }
          : {}),
      }}
    >
      {DB_STATUS_VALUES.map((status) => (
        <MenuItem key={status} value={status}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: DB_STATUS_COLORS[status],
              }}
            />
            <Typography variant="body2">{t(dbStatusLabelKey(status))}</Typography>
          </Box>
        </MenuItem>
      ))}
    </Select>
  );
};

export default DbStatusSelector;
